
import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { X, Check, RefreshCw, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';

const ConfirmationPage = () => {
  const [searchParams] = useSearchParams();
  const status = searchParams.get('status');
  const email = searchParams.get('email');
  const token = searchParams.get('token');
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  // Handle token confirmation
  useEffect(() => {
    async function confirmToken() {
      if (token) {
        try {
          // Call the confirm endpoint with the token
          // We need to use a different approach since query is not supported
          // Redirect to the API endpoint
          window.location.href = `${window.location.origin}/api/email-subscribe/confirm?token=${token}`;
          return; // Stop execution since we're redirecting to the function URL
        } catch (error) {
          console.error('Error confirming subscription:', error);
          window.location.href = '/confirmation?status=error';
        }
      }
    }
    
    if (token) {
      confirmToken();
    }
  }, [token]);
  
  // Reset resend message after 5 seconds
  useEffect(() => {
    if (resendSuccess) {
      const timer = setTimeout(() => {
        setResendSuccess(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [resendSuccess]);

  const handleResendConfirmation = async () => {
    if (!email || isResending) return;
    
    setIsResending(true);
    
    try {
      const response = await supabase.functions.invoke('email-subscribe/resend', {
        body: JSON.stringify({ email }),
        method: 'POST',
      });
      
      if (response.error) {
        throw new Error(response.error);
      }
      
      setResendSuccess(true);
    } catch (error) {
      console.error('Error resending confirmation:', error);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col items-center justify-center p-4">
      <Card className="max-w-md w-full p-6 shadow-md">
        {token && (
          <div className="text-center">
            <div className="bg-blue-100 rounded-full h-16 w-16 flex items-center justify-center mx-auto mb-4">
              <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Confirming your subscription</h1>
            <p className="text-neutral-600 mb-6">
              Please wait while we confirm your email address...
            </p>
          </div>
        )}
        {status === 'success' && (
          <div className="text-center">
            <div className="bg-green-100 rounded-full h-16 w-16 flex items-center justify-center mx-auto mb-4">
              <Check className="h-8 w-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold mb-2">You're in!</h1>
            <p className="text-neutral-600 mb-6">
              Thanks for confirming your email. You've been added to our newsletter list.
            </p>
            <Link to="/">
              <Button className="w-full">Return to Home</Button>
            </Link>
          </div>
        )}

        {status === 'invalid' && (
          <div className="text-center">
            <div className="bg-red-100 rounded-full h-16 w-16 flex items-center justify-center mx-auto mb-4">
              <X className="h-8 w-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Invalid Link</h1>
            <p className="text-neutral-600 mb-6">
              This confirmation link is invalid or has already been used.
            </p>
            <Link to="/">
              <Button className="w-full">Return to Home</Button>
            </Link>
          </div>
        )}

        {status === 'expired' && (
          <div className="text-center">
            <div className="bg-amber-100 rounded-full h-16 w-16 flex items-center justify-center mx-auto mb-4">
              <RefreshCw className="h-8 w-8 text-amber-600" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Link Expired</h1>
            <p className="text-neutral-600 mb-4">
              This confirmation link has expired. Would you like us to send a new one?
            </p>
            
            {resendSuccess ? (
              <div className="text-green-600 mb-4">
                A new confirmation email has been sent! Please check your inbox.
              </div>
            ) : (
              <Button 
                onClick={handleResendConfirmation}
                disabled={isResending || !email}
                className="w-full mb-4"
              >
                {isResending ? 'Sending...' : 'Resend Confirmation'}
              </Button>
            )}
            
            <Link to="/">
              <Button variant="outline" className="w-full">
                Return to Home
              </Button>
            </Link>
          </div>
        )}

        {status === 'error' && (
          <div className="text-center">
            <div className="bg-red-100 rounded-full h-16 w-16 flex items-center justify-center mx-auto mb-4">
              <X className="h-8 w-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Something Went Wrong</h1>
            <p className="text-neutral-600 mb-6">
              We encountered an issue while processing your confirmation.
              Please try again or contact support.
            </p>
            <Link to="/">
              <Button className="w-full">Return to Home</Button>
            </Link>
          </div>
        )}

        {!status && (
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-2">Invalid Request</h1>
            <p className="text-neutral-600 mb-6">
              Something went wrong with this confirmation request.
            </p>
            <Link to="/">
              <Button className="w-full">Return to Home</Button>
            </Link>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ConfirmationPage;
